import {useMemo, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useTranslation } from "../../common/translation-context";
import {
  APP_VIEW,
  APP_ACTIONS,
  CCM_EDITABLE,
  otherThanJDWINCountries,
  EXTENSION_COLUMNS,
  ROLES,
  CODE_COUNTRY_FULL_NAME,
} from "../../constants/constant";
import {
  fetchCCMRequests,
  approveRequest,
  rejectRequest,
  sendToBP as sendToBPAction,
  saveCCMData,
  filterCCMRequests,
} from "../../store/slices/extension";
import { getUserCountry, getUserRole } from "../../utils/utils";
import ModalMessageFunction from "../../common/modal-message-function";
import ToastMessageFunction from "../../common/toast-message-function";
import SearchBar from "../../common/SearchBar";
import { useContext } from "react";
import ModalContext from "../../common/modal-context";
import ExtDataGrid from "../../common/data-grid/ext-data-grid";

const JDFPage = () => {
  const dispatch = useDispatch();
  const ctx = useContext(ModalContext);
  const location = useLocation();
  const userData = useSelector((state) => state.userState.userData);
  const defaultCountry =
    getUserRole(userData) !== ROLES.CCM && getUserCountry(userData);
  const { translate: t } = useTranslation();

  const { dropdowns } = useSelector((state) => state.sharedExtension);
  const {
    requests: ccmRequests,
    requestsRef: ccmRequestsRef,
    loading: ccmLoading,
    error: ccmError,
    actionLoading,
  } = useSelector((state) => state.ccmExtension);

  const [actionPerformed, setActionPerformed] = useState(false);
  const [requestType, setRequestType] = useState("");
  const [apiData, setApiData] = useState({});
  const [isCountryChanged, setIsCountryChanged] = useState(false);
  const [isExtensionChanged, setIsExtensionChanged] = useState(false);
  const [selectedExtensionType, setSelectedExtensionType] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
const [selectedReferenceContract, setSelectedReferenceContract] = useState("");
  const [selectedSerialNos, setSelectedSerialNos] = useState([]);
  const processAction = (action, requestType) => {
    setActionPerformed(action);
    setRequestType(requestType);
  };

  const modalManager = ModalMessageFunction(processAction);
  const toastManager = ToastMessageFunction();
  const showToast = useCallback(
    (message, severity) => {
      toastManager.getToast(message, severity);
    },
    [toastManager],
  );

  const getCountryCode = useCallback(
    (value) => {
      let listData = dropdowns.countryCode?.find((res) => res[0] === value);
      return listData?.length > 0 ? listData[1] : null;
    },
    [dropdowns.countryCode],
  );
  // Filtered reference contract options based on country, extension type, and selected serial numbers
  const filteredReferenceContractOptions = useMemo(() => {
    if (!ccmRequestsRef?.length) return [];
    
    const countryCode = getCountryCode(selectedCountry);
    let filteredData = ccmRequestsRef;
    
    // Country filter
    if (selectedCountry === "JDWIN") {
      filteredData = filteredData.filter((res) =>
        selectedExtensionType
          ? !otherThanJDWINCountries.includes(res?.country) && res?.extensionType === selectedExtensionType
          : !otherThanJDWINCountries.includes(res?.country)
      );
    } else if (selectedCountry && countryCode) {
      filteredData = filteredData.filter((res) =>
        selectedExtensionType
          ? res?.country === countryCode && res?.extensionType === selectedExtensionType
          : res?.country === countryCode
      );
    } else if (selectedExtensionType) {
      filteredData = filteredData.filter((res) =>
        res?.extensionType === selectedExtensionType
      );
    }
    
    // Serial number filter - if serial numbers are selected, only show financing references that match
    if (selectedSerialNos && selectedSerialNos.length > 0) {
      filteredData = filteredData.filter((res) => {
        const rowSerial = String(res?.serialNo ?? "").toLowerCase();
        return selectedSerialNos.some((sn) => rowSerial === String(sn).toLowerCase());
      });
    }
    
    const filteredRefs = [...new Set(
      filteredData.map((r) => String(r.referenceContract ?? "")).filter(Boolean)
    )];
    
    // Merge with selected value to keep it visible
    if (selectedReferenceContract && !filteredRefs.includes(selectedReferenceContract)) {
      filteredRefs.push(selectedReferenceContract);
    }
    
    return filteredRefs.sort();
  }, [ccmRequestsRef, selectedCountry, selectedExtensionType, selectedReferenceContract, selectedSerialNos, getCountryCode]);

  // Filtered serial number options based on country, extension type, and reference contract
  const filteredSerialNoOptions = useMemo(() => {
    if (!ccmRequestsRef?.length) return [];
    
    const countryCode = getCountryCode(selectedCountry);
    let filteredData = ccmRequestsRef;
    
    // Country filter
    if (selectedCountry === "JDWIN") {
      filteredData = filteredData.filter((res) =>
        selectedExtensionType
          ? !otherThanJDWINCountries.includes(res?.country) && res?.extensionType === selectedExtensionType
          : !otherThanJDWINCountries.includes(res?.country)
      );
    } else if (selectedCountry && countryCode) {
      filteredData = filteredData.filter((res) =>
        selectedExtensionType
          ? res?.country === countryCode && res?.extensionType === selectedExtensionType
          : res?.country === countryCode
      );
    } else if (selectedExtensionType) {
      filteredData = filteredData.filter((res) =>
        res?.extensionType === selectedExtensionType
      );
    }
    
    // Financing reference filter
    if (selectedReferenceContract) {
      filteredData = filteredData.filter((res) =>
        String(res?.referenceContract ?? "").toLowerCase().includes(String(selectedReferenceContract).toLowerCase())
      );
    }
    
    // Extract unique serial numbers from filtered data
    const filteredSerials = [...new Set(
      filteredData.map((r) => String(r.serialNo ?? "")).filter(Boolean)
    )];
    
    // Merge with already-selected values to keep them visible in dropdown
    const mergedSerials = [...new Set([...filteredSerials, ...(selectedSerialNos || [])])];
    
    return mergedSerials.sort();
  }, [ccmRequestsRef, selectedCountry, selectedExtensionType, selectedReferenceContract, selectedSerialNos, getCountryCode]);
  useEffect(() => {
    dispatch(fetchCCMRequests());
  }, [dispatch]);

  useEffect(() => {
    if (ccmError) {
      showToast(ccmError, "error");
    }
  }, [ccmError, showToast]);
  useEffect(() => {
    const countryNameFromState = location?.state?.countryName;
    const countryCodeFromState =
      location?.state?.countryCode || location?.state?.country;
    if (countryNameFromState) {
      setSelectedCountry(countryNameFromState);
      setIsCountryChanged(true);
    } else if (countryCodeFromState) {
      const mappedName =
        CODE_COUNTRY_FULL_NAME[countryCodeFromState] || countryCodeFromState;
      setSelectedCountry(mappedName);
      setIsCountryChanged(true);
    }
  }, [location?.state]);

  useEffect(() => {
    if (isCountryChanged) {
      handleCountryChange();
    }
    if (isExtensionChanged) {
      handleExtensionChange();
    }
  }, [
    isCountryChanged,
    isExtensionChanged,
    selectedCountry,
    selectedExtensionType,
    selectedReferenceContract,
    selectedSerialNos
  ]);

  useEffect(() => {
    if (actionPerformed) {
     setActionPerformed(false)
      ctx.setIsDisable(true);

      if (requestType === "approve") {
        dispatch(approveRequest({ ...apiData, view: APP_VIEW.CCM }))
          .unwrap()
          .then(() => {
            dispatch(fetchCCMRequests());
            showToast(t("requestExtended"), "success");
            ctx.setIsDisable(false);
          })
          .catch((error) => {
            showToast(error || t("requestExtensionFail"), "error");
            ctx.setIsDisable(false);
          });
      } else if (requestType === "sendToBp") {
        dispatch(sendToBPAction(apiData))
          .unwrap()
          .then(() => {
            showToast(t("sentToBp"), "success");
            ctx.setIsDisable(false);
          })
          .catch((error) => {
            showToast(error || t("sentToBpFail"), "error");
            ctx.setIsDisable(false);
          });
      } else if (requestType === "reject") {
        dispatch(rejectRequest({ ...apiData, view: APP_VIEW.CCM }))
          .unwrap()
          .then(() => {
            dispatch(fetchCCMRequests());
            showToast(t("requestRejected"), "success");
            ctx.setIsDisable(false);
          })
          .catch((error) => {
            showToast(error || t("requestRejectionFail"), "error");
            ctx.setIsDisable(false);
          });
      }
    }
  }, [actionPerformed, apiData, requestType, dispatch,]);

  const getModalAction = (requestType, requestStatus, isDateExceed) => {
    modalManager.getModal(requestType, requestStatus, isDateExceed);
  };

  const reject = (id, requestStatus) => {
    setApiData({ id, requestStatus });
    getModalAction(APP_ACTIONS.REJECT, requestStatus);
  };

  const sendToBP = (id, requestStatus) => {
    setApiData({ id, requestStatus });
    getModalAction(APP_ACTIONS.SENDTOBP, requestStatus);
  };

  const approve = (id, requestStatus, isDateExceed = false) => {
    setApiData({ id, requestStatus });
    getModalAction(APP_ACTIONS.APPROVE, requestStatus, isDateExceed);
  };

  const save = (data, row) => {
    if (Object.keys(data).length > 0) {
      dispatch(saveCCMData(data))
        .unwrap()
        .then((res) => {
          dispatch(fetchCCMRequests());
          showToast(res.message || t("saveDataSuccessfully"), "success");
        })
        .catch((error) => {
          showToast(error || t("somethingWentWrong"), "error");
        });
    } else {
      showToast(t("noDataToSave"), "error");
    }
  };

  const handleCountryChange = () => {
    const value = selectedCountry;
    const countryCode = getCountryCode(value);
    
    if (ccmRequestsRef?.length && dropdowns.countryCode?.length > 0) {
      dispatch(filterCCMRequests({
        country: value,
        extensionType: selectedExtensionType,
        countryCode: countryCode,
        otherThanJDWINCountries,
        referenceContract: selectedReferenceContract,
        serialNos: selectedSerialNos,
      }));
    }
  };
const onReferenceContractChange = (value) => {
  setSelectedReferenceContract(value);
  const countryCode = getCountryCode(selectedCountry);
  if (ccmRequestsRef?.length && dropdowns.countryCode?.length > 0) {
    dispatch(filterCCMRequests({
      country: selectedCountry,
      extensionType: selectedExtensionType,
      countryCode,
      otherThanJDWINCountries,
      referenceContract: value,
      serialNos: selectedSerialNos,
    }));
  }
};

  const onSerialNoChange = (value) => {
    setSelectedSerialNos(value);
    const countryCode = getCountryCode(selectedCountry);
    if (ccmRequestsRef?.length && dropdowns.countryCode?.length > 0) {
      dispatch(filterCCMRequests({
        country: selectedCountry,
        extensionType: selectedExtensionType,
        countryCode,
        otherThanJDWINCountries,
        referenceContract: selectedReferenceContract,
        serialNos: value,
      }));
    }
  };
  const handleExtensionChange = () => {
    const value = selectedExtensionType;
    const countryCode = getCountryCode(selectedCountry);
    
    if (ccmRequestsRef?.length && dropdowns.countryCode?.length > 0) {
      dispatch(filterCCMRequests({
        country: selectedCountry,
        extensionType: value,
        countryCode: countryCode,
        otherThanJDWINCountries,
        referenceContract: selectedReferenceContract,
        serialNos: selectedSerialNos,
      }));
    }
  };

  const onCountryChange = (value) => {
    setSelectedCountry(value);
    setSelectedReferenceContract("");
    setSelectedSerialNos([]);
    setIsCountryChanged(true);
  };

  const onExtensionChange = (value) => {
    setSelectedExtensionType(value);
    setSelectedReferenceContract("");
    setSelectedSerialNos([]);
    setIsExtensionChanged(true);
  };

  const onClear = () => {
    setSelectedCountry(defaultCountry);
    setSelectedExtensionType("");
    setSelectedReferenceContract("");
    setSelectedSerialNos([]);
    setIsCountryChanged(true);
  };

  useEffect(() => {
    handleCountryChange();
  }, [ccmRequestsRef]);

  return (
    <div>
      <SearchBar
        sessionKey={{ ext: "ccmExtension", cty: "ccmCountry" }}
        extensionOptions={dropdowns.extensionTypes}
        countryOptions={dropdowns.countries}
        selectedCountry={selectedCountry}
        selectedExtension={selectedExtensionType}
        onExtensionChange={onExtensionChange}
        onCountryChange={onCountryChange}
        referenceContractOptions={filteredReferenceContractOptions}
        selectedReferenceContract={selectedReferenceContract}
        onReferenceContractChange={onReferenceContractChange}
        serialNoOptions={filteredSerialNoOptions}
        selectedSerialNos={selectedSerialNos}
        onSerialNoChange={onSerialNoChange}
        onClear={onClear}
      />
      <div className="extension-header">
        <h3>
          {t("approvalRequests")} ({ccmRequests?.length})
        </h3>
      </div>
      {modalManager.modal}
      {toastManager.toast}
      <ExtDataGrid
        view={APP_VIEW.CCM}
        columns={EXTENSION_COLUMNS}
        rows={ccmRequests}
        editableFields={CCM_EDITABLE}
        reject={reject}
        approve={approve}
        sendToBP={sendToBP}
        save={save}
        isLoaderVisible={ccmLoading || actionLoading}
        isDisabled={actionLoading}
        showToast={showToast}
      />
      {toastManager.toast}
    </div>
  );
};

export default JDFPage;
